import { useState } from 'react';
import { Link } from 'react-router-dom';
import { products } from '../../../mocks/products';

export default function HomeProductSection() {
  const featuredProducts = products.slice(0, 6);
  const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>({});

  const handleImageError = (productId: string) => {
    setImageErrors(prev => ({ ...prev, [productId]: true }));
  };

  return (
    <section className="py-20 bg-gradient-to-br from-orange-50 via-amber-50 to-rose-50 relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-10 left-10 w-40 h-40 bg-orange-300/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-10 right-10 w-48 h-48 bg-rose-300/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg mb-4">
            <i className="ri-shopping-bag-fill text-orange-500"></i>
            <span className="text-sm font-bold text-gray-700">Premium Cat Products</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-4">
            <span className="bg-gradient-to-r from-orange-600 to-rose-600 bg-clip-text text-transparent">
              Shop
            </span>{' '}
            Top-Rated Products
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Discover premium toys, treats, and essentials trusted by cat lovers worldwide
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {featuredProducts.map((product, index) => (
            <div
              key={product.id}
              className="group relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Product Image */}
              <div className="relative aspect-square overflow-hidden bg-gray-50">
                <img
                  src={imageErrors[product.id] 
                    ? 'https://public.readdy.ai/ai/img_res/d479523c3c25536ef9d96bbfce1c0683.jpg'
                    : product.image
                  }
                  alt={product.name}
                  className="w-full h-full object-contain p-6 group-hover:scale-110 transition-transform duration-500"
                  onError={() => handleImageError(product.id)}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Badges */}
                <div className="absolute top-4 left-4 flex flex-col gap-2">
                  {product.badge && (
                    <span className="px-3 py-1 bg-gradient-to-r from-orange-500 to-rose-500 text-white text-xs font-bold rounded-full shadow-lg">
                      {product.badge}
                    </span>
                  )}
                  <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-gray-900 text-xs font-bold rounded-full shadow-lg flex items-center gap-1">
                    <i className="ri-medal-fill text-orange-500"></i>
                    Highly Rated on Chewy
                  </span>
                </div>

                {/* Quick View Button */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <Link
                    to={product.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 bg-white text-gray-900 font-bold rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 whitespace-nowrap flex items-center gap-2"
                  >
                    <i className="ri-shopping-cart-fill text-orange-500"></i>
                    <span>View on Chewy</span>
                  </Link>
                </div>
              </div>

              {/* Product Info */}
              <div className="p-6">
                {/* Brand */}
                <div className="text-sm font-bold text-orange-600 mb-2">{product.brand}</div>

                {/* Product Name */}
                <h3 className="text-lg font-black text-gray-900 mb-3 line-clamp-2 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-orange-600 group-hover:to-rose-600 transition-all duration-300">
                  {product.name}
                </h3>

                {/* Price */}
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-3xl font-black text-gray-900">{product.price}</span>
                  {product.originalPrice && (
                    <span className="text-lg text-gray-400 line-through">{product.originalPrice}</span>
                  )}
                </div>

                {/* CTA */}
                <Link
                  to={product.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-rose-500 text-white font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 whitespace-nowrap group/btn"
                >
                  <span>Shop Now</span>
                  <i className="ri-arrow-right-line text-xl group-hover/btn:translate-x-1 transition-transform duration-300"></i>
                </Link>
              </div>

              {/* Decorative Corner */}
              <div className="absolute -bottom-8 -right-8 w-24 h-24 bg-gradient-to-br from-orange-400 to-rose-400 rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-500"></div>
            </div>
          ))}
        </div>

        {/* Trust Bar */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-lg">
                <i className="ri-shield-check-fill text-2xl text-white"></i>
              </div>
              <div className="text-sm font-bold text-gray-900">Verified Products</div>
              <div className="text-xs text-gray-500">100% Authentic</div>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
                <i className="ri-truck-fill text-2xl text-white"></i>
              </div>
              <div className="text-sm font-bold text-gray-900">Fast Shipping</div>
              <div className="text-xs text-gray-500">2-Day Delivery</div>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center shadow-lg">
                <i className="ri-price-tag-3-fill text-2xl text-white"></i>
              </div>
              <div className="text-sm font-bold text-gray-900">Best Prices</div>
              <div className="text-xs text-gray-500">Price Match Guarantee</div>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                <i className="ri-customer-service-2-fill text-2xl text-white"></i>
              </div>
              <div className="text-sm font-bold text-gray-900">24/7 Support</div>
              <div className="text-xs text-gray-500">Always Here to Help</div>
            </div>
          </div>
        </div>

        {/* Main CTA */}
        <div className="text-center">
          <Link
            to="/shop"
            className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-orange-500 to-rose-500 text-white text-xl font-black rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 whitespace-nowrap group"
          >
            <i className="ri-shopping-bag-fill text-2xl"></i>
            <span>Browse All Products</span>
            <i className="ri-arrow-right-line text-2xl group-hover:translate-x-2 transition-transform duration-300"></i>
          </Link>
          <p className="text-gray-600 mt-6 text-lg">
            Join 50K+ happy customers shopping with us 🛍️
          </p>
        </div>
      </div>
    </section>
  );
}
